'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { instrumentKey, normalizeExchange, normalizeProduct, type InstrumentIdentity } from '@/lib/instrument';

interface GroupLike {
  items?: Array<{ id?: string; symbol: string; exchange?: string; product?: string }>;
}

/**
 * AUD21 + D01: single source of truth for watchlist membership across markets /
 * detail / search. Aggregates every group's items into an identity key →
 * item id map so toggling can remove from whichever group actually holds the
 * identity. Identity key = `SYMBOL|exchange|product`（现货/永续可分别保存）。
 * Guests get an empty set without erroring (query is disabled).
 */
export function useWatchlistMembership() {
  const user = useAppStore((s) => s.user);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['watchlist', 'symbols'],
    queryFn: async () => {
      const groups = await api.get<GroupLike[]>('/watchlist');
      const map = new Map<string, string>();
      for (const g of groups) {
        for (const it of g.items ?? []) {
          if (!it.symbol) continue;
          const key = instrumentKey({
            symbol: it.symbol,
            exchange: normalizeExchange(it.exchange),
            product: normalizeProduct(it.product) ?? 'futures',
          });
          if (!map.has(key)) map.set(key, it.id ?? '');
        }
      }
      return map;
    },
    enabled: !!user,
    staleTime: 30_000,
    retry: 1,
  });

  /** 按 symbol 判断（向后兼容：匹配任一身份） */
  const isStarred = (symbol: string): boolean => {
    if (!data) return false;
    const sym = symbol.toUpperCase();
    for (const k of data.keys()) {
      if (k.startsWith(`${sym}|`)) return true;
    }
    return data.has(sym);
  };

  /** D01：按完整身份（symbol+exchange+product）判断；兼容无 exchange 的旧数据。 */
  const isStarredIdentity = (instr: InstrumentIdentity): boolean => {
    if (!data) return false;
    const key = instrumentKey(instr);
    if (data.has(key)) return true;
    // 旧数据未记录 exchange（key 为 SYMBOL||product）：同 symbol+product 视为已添加
    const product = normalizeProduct(instr.product) ?? 'futures';
    return data.has(`${instr.symbol.toUpperCase()}||${product}`);
  };

  const toggle = useMutation({
    mutationFn: async (symbol: string) => {
      const itemId = data?.get(symbol);
      if (itemId) {
        await api.delete(`/watchlist/items/${itemId}`);
      } else {
        await api.post('/watchlist/quick-add', { symbol });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['watchlist'] });
      qc.invalidateQueries({ queryKey: ['watchlist', 'symbols'] });
    },
  });

  return { isStarred, isStarredIdentity, toggle, loading: !user || isLoading, user };
}
